/*
 *
 *  * Copyright (c) [hoarfroster-frontend] 2026. All Rights Reserved.
 *  *
 *  * Last Modified on Sep 12, 2026 by hoarfroster
 *
 */

import { MouseEvent, ReactNode, ButtonHTMLAttributes, forwardRef } from 'react';
import Spinner from '@components/spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'small' | 'medium' | 'large';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    children: ReactNode;
    disabled?: boolean;
    onClick?: (ev: MouseEvent<HTMLButtonElement>) => void;
    type?: 'button' | 'submit' | 'reset';
    /** Material Symbols icon name rendered before the label. */
    icon?: string;
    variant?: ButtonVariant;
    size?: ButtonSize;
    /** Shows a spinner and blocks interaction. */
    loading?: boolean;
    className?: string;
}

const variantClasses: Record<ButtonVariant, string> = {
    primary: 'bg-theme-active text-theme-active-contrast border-theme-active',
    secondary: 'bg-theme-button-secondary text-theme-text border-theme-border',
    ghost: 'bg-transparent text-theme-text-light border-transparent',
    danger: 'bg-theme-error text-theme-active-contrast border-theme-error',
};

const hoverClasses: Record<ButtonVariant, string> = {
    primary: 'hover:bg-theme-button-hover',
    secondary: 'hover:bg-theme-button-secondary-hover',
    ghost: 'hover:bg-theme-hover',
    danger: 'hover:bg-theme-error/90',
};

const sizeClasses: Record<ButtonSize, string> = {
    small: 'text-xs px-3 py-1.5 min-h-8',
    medium: 'text-sm px-4 py-2 min-h-10',
    large: 'text-base px-5 py-3 min-h-12',
};

/**
 * App-wide button. Custom `hover:` classes in `className` replace the default
 * variant hover instead of fighting it.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
    {
        children,
        disabled = false,
        onClick,
        type = 'button',
        icon,
        variant = 'primary',
        size = 'medium',
        loading = false,
        className = '',
        ...rest
    },
    ref
) {
    const isDisabled = disabled || loading;
    const customClasses = className.split(' ').filter((part) => part.trim() !== '');
    const hasCustomHover = customClasses.some((part) => part.startsWith('hover:'));

    const baseClasses = [
        'inline-flex',
        'items-center',
        'justify-center',
        'gap-2',
        'rounded-lg',
        'border',
        'font-semibold',
        'shadow-e1',
        'transition-[background-color,border-color,box-shadow,opacity,transform]',
        'active:scale-[0.98]',
        'focus-visible:ring-4',
        'focus-visible:ring-theme-focus/20',
        'focus-visible:outline-none',
        ...variantClasses[variant].split(' '),
        ...sizeClasses[size].split(' '),
        ...customClasses,
        ...(isDisabled ? ['cursor-not-allowed', 'opacity-60'] : []),
        ...(hasCustomHover ? [] : hoverClasses[variant].split(' ')),
    ].join(' ');

    return (
        <button ref={ref} className={baseClasses} onClick={onClick} disabled={isDisabled} type={type} {...rest}>
            {loading ? (
                <Spinner size="sm" />
            ) : (
                icon && <span className="material-symbols-rounded text-base">{icon}</span>
            )}
            {children != null && <span>{children}</span>}
        </button>
    );
});

export interface IconButtonProps extends Omit<ButtonProps, 'children' | 'icon'> {
    /** Material Symbols icon name. */
    icon: string;
    /** Required accessible name; icon-only buttons have no visible text. */
    'aria-label': string;
}

/** Square icon-only button for toolbars and dense layouts. */
export function IconButton({ icon, size = 'medium', className = '', ...rest }: IconButtonProps) {
    const dimension = size === 'large' ? 'h-12 w-12' : size === 'small' ? 'h-8 w-8' : 'h-10 w-10';
    return (
        <Button size={size} className={`!px-0 ${dimension} ${className}`} {...rest}>
            <span className="material-symbols-rounded text-xl leading-none">{icon}</span>
        </Button>
    );
}
